import { useCallback, useContext, useState, useEffect } from 'react';

import uniq from 'lodash/uniq';

import { sendFormRequest } from '@vyce/core/src/api/forms';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { usePhoneListPaste } from '@vyce/core/src/hooks/usePhoneListPaste';
import {
  ChatUser,
  CompanyWorkerItem,
  FormDoc,
  FormParticipantInvite,
  User,
} from '@vyce/core/src/types';

import { useForm } from 'react-hook-form';

/**
 * Matches the invites cap. The forms endpoint has no documented participant limit — confirm
 * with backend before raising it.
 */
const FORM_RECIPIENTS_LIMIT = 500;

interface Form {
  email?: string;
  phone?: string;
  clockInBlock?: boolean;
  signature?: boolean;
  folderId?: string | null;
}

export const useFormRecipients = ({
  user,
  companyId,
  form,
  initialFolderId = null,
}: {
  user: User;
  companyId: string;
  form?: FormDoc | null;
  initialFolderId?: string | null;
}) => {
  const methods = useForm<Form>({
    mode: 'all',
    defaultValues: {
      email: '',
      phone: '',
      clockInBlock: false,
      signature: false,
      folderId: initialFolderId,
    },
  });
  const [isPhoneInputDisabled, setIsPhoneInputDisabled] = useState(true);
  const [emails, setEmails] = useState<string[]>([]);
  const [phones, setPhones] = useState<string[]>([]);
  const [selectedWorkers, setSelectedWorkers] = useState<CompanyWorkerItem[]>([]);
  const { showNotification } = useContext(NotificationContext);
  const userData: ChatUser = {
    uid: user.uuid,
    name: user.first_name + ' ' + user.last_name,
    email: user.email,
    phone: user.phone,
  };
  const { trigger, getValues, setValue } = methods;

  const addPhones = useCallback(
    (list: string[]) => setPhones(value => uniq([...value, ...list])),
    []
  );

  const bulkPaste = usePhoneListPaste({
    existing: phones,
    onAdd: addPhones,
    limit: FORM_RECIPIENTS_LIMIT,
  });

  /**
   * `extraParticipants` are people resolved elsewhere (e.g. everyone clocked in at a location);
   * `skippedCount` is how many of those couldn't be sent to, so the toast can say so.
   */
  const sendFormRecipients = async (
    formId: string,
    {
      extraParticipants = [],
      skippedCount = 0,
    }: { extraParticipants?: FormParticipantInvite[]; skippedCount?: number } = {}
  ) => {
    try {
      const participants: FormParticipantInvite[] = [];
      emails.forEach(email => {
        participants.push({ email, phone: '' });
      });
      phones.forEach(phone => {
        participants.push({ email: '', phone });
      });
      selectedWorkers.forEach(worker => {
        // sendFormToUsers requires email or phone per participant
        if (!worker.email && !worker.phone) {
          return;
        }
        if (worker.email && emails.includes(worker.email)) {
          return;
        }
        if (worker.phone && phones.includes(worker.phone)) {
          return;
        }
        participants.push({
          email: worker.email ?? '',
          phone: worker.phone ?? '',
          name: `${worker.first_name} ${worker.last_name}`.trim(),
          uid: worker.user_id,
          // Persisted on the participant doc so every recipients list shows the same profile data
          ...(worker.avatar ? { avatarUrl: worker.avatar } : {}),
          ...(worker.ni_number ? { niNumber: worker.ni_number } : {}),
        });
      });
      extraParticipants.forEach(participant => {
        const isDuplicate = participants.some(
          existing =>
            (!!participant.uid && existing.uid === participant.uid) ||
            (!!participant.email && existing.email === participant.email) ||
            (!!participant.phone && existing.phone === participant.phone)
        );
        if (!isDuplicate) {
          participants.push(participant);
        }
      });
      const result = await sendFormRequest({
        companyId,
        formId,
        user: userData,
        participants,
      });
      if (result.success) {
        setSelectedWorkers([]);
        const skippedPeople = skippedCount === 1 ? '1 person has' : `${skippedCount} people have`;
        const skippedNote = skippedCount ? ` ${skippedPeople} no email or phone, so they were skipped.` : '';
        const sentPeople = participants.length === 1 ? '1 person' : `${participants.length} people`;
        const message = extraParticipants.length
          ? `Form sent to ${sentPeople}.${skippedNote}`
          : 'Form has been sent';
        showNotification({ message, options: { variant: 'success' } });
      }
    } catch (error) {
      console.error(error);
      showNotification({ message: 'Failed to send form', options: { variant: 'error' } });
    }
  };

  const toggleWorker = (worker: CompanyWorkerItem) => {
    setSelectedWorkers(value =>
      value.some(item => item.user_id === worker.user_id)
        ? value.filter(item => item.user_id !== worker.user_id)
        : [...value, worker]
    );
  };

  const handleEmailFieldKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      addEmailToList();
    }
  };

  const handlePhoneFieldKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      addPhoneToList();
    }
  };

  // Use what trigger returns — `errors` from the render closure is a tick behind it
  const addEmailToList = async () => {
    const isValid = await trigger('email');
    const email = getValues('email');
    if (!isValid || !email) {
      return;
    }
    setEmails(value => uniq([...value, email]));
    setValue('email', '');
  };

  const addPhoneToList = async () => {
    const isValid = await trigger('phone');
    const phone = getValues('phone');
    if (!isValid || !phone) {
      return;
    }
    addPhones([phone]);
    setValue('phone', '');
    // The paste counts describe an earlier action and are stale now
    bulkPaste.clear();
  };

  const deleteContact = (contact: string) => {
    setEmails(emails.filter(email => email !== contact));
    setPhones(phones.filter(phone => phone !== contact));
  };

  const clearContacts = () => {
    setEmails([]);
    setPhones([]);
    setSelectedWorkers([]);
    bulkPaste.clear();
  };

  useEffect(() => {
    if (form) {
      setValue('clockInBlock', !!form.clockInBlock);
      setValue('signature', !!form.signature);
      setValue('folderId', form.folderId || null);
    }
  }, [form]);

  return {
    isPhoneInputDisabled,
    handleEmailFieldKeyDown,
    handlePhoneFieldKeyDown,
    addEmailToList,
    addPhoneToList,
    deleteContact,
    clearContacts,
    emails,
    phones,
    selectedWorkers,
    toggleWorker,
    setIsPhoneInputDisabled,
    recipientsFormMethods: methods,
    sendFormRecipients,
    bulkPaste: {
      result: bulkPaste.result,
      onPasteList: bulkPaste.handlePasteList,
      onDismiss: bulkPaste.dismiss,
    },
    bulkLimitHint: `you can add up to ${FORM_RECIPIENTS_LIMIT} recipients`,
  };
};
